import { HttpError } from '@/lib/httpError'

/**
 * License verification response from API
 */
export interface LicenseVerificationResponse {
  IsValid: boolean
  Product: string
  LicenseType: string
  CustomerName: string
  Email: string
  Organization: string
  ExpiresAt: string
  MaxFlows: number
  ErrorMessage: string
  DaysUntilExpiration: number
}

/**
 * License API Service
 * Handles license verification and related operations
 */
class LicenseApiService {
  private baseUrl = '/api/v2/licenses'

  /**
   * Verify a license key
   * @param licenseKey - The license key to verify
   * @returns Verification response
   * @throws HttpError if verification fails
   */
  async verifyLicense(licenseKey: string): Promise<LicenseVerificationResponse> {
    const response = await fetch(`${this.baseUrl}/verification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ license: licenseKey }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new HttpError(errorText || 'Failed to verify license', response.status)
    }

    const data: LicenseVerificationResponse = await response.json()

    // Check if license is valid
    if (!data.IsValid) {
      throw new Error(data.ErrorMessage || 'License is not valid')
    }

    return data
  }
}

export const licenseApiService = new LicenseApiService()
